import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  InputBase,
  Tooltip,
} from '@mui/material';
import {
  Article,
  RssFeed,
  VpnKey,
  BarChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  Search,
  Person,
  Logout,
  AdminPanelSettings,
  Folder,
  Inbox,
  Star,
  ContentCopy,
} from '@mui/icons-material';
import { LayoutGroup, motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import ThemeSwitcher from '../common/ThemeSwitcher';
import { articleApi, groupApi } from '../../services/saasApi';
import { SubscriptionGroup, UnreadCountsResponse } from '../../services/types';

const SIDEBAR_ROUTES = [
  { labelKey: 'nav.articles', path: '/articles', icon: Article },
  { labelKey: 'nav.subscriptions', path: '/subscriptions', icon: RssFeed },
  { labelKey: 'nav.api_tokens', path: '/settings/api-tokens', icon: VpnKey },
  { labelKey: 'nav.stats', path: '/stats', icon: BarChart },
  { labelKey: 'nav.settings', path: '/settings', icon: Settings },
];

const COLLAPSED_KEY = 'sidebarCollapsed';

export function getSidebarWidth(collapsed: boolean): string {
  return collapsed ? '64px' : '240px';
}

export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // ignore
  }
}

interface SidebarProps {
  totalUnread: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const UserMenuInline: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const avatarEl = (
    <IconButton onClick={handleClick} size="small" sx={{ p: 0.5 }} aria-label="用户菜单">
      <Avatar
        src={
          user?.avatar_url &&
          (user.avatar_url.startsWith('http://') || user.avatar_url.startsWith('https://'))
            ? user.avatar_url
            : undefined
        }
        sx={{ width: 32, height: 32, bgcolor: 'var(--accent)' }}
      >
        {user?.username?.[0]?.toUpperCase() || <Person />}
      </Avatar>
    </IconButton>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip title={user?.username || ''} placement="right">
          {avatarEl}
        </Tooltip>
      ) : (
        avatarEl
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{user?.username}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        {user?.is_admin && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/admin');
            }}
          >
            <AdminPanelSettings fontSize="small" sx={{ mr: 1 }} />
            {t('nav.admin')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/settings');
          }}
        >
          <Settings fontSize="small" sx={{ mr: 1 }} />
          {t('nav.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            logout();
            navigate('/login');
          }}
        >
          <Logout fontSize="small" sx={{ mr: 1 }} />
          {t('auth.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

interface CategoryItemProps {
  active?: boolean;
  icon: React.ReactNode;
  label: string;
  count?: number;
  onClick: () => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({ active, icon, label, count, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      cursor: 'pointer',
      height: 32,
      pl: 1.75,
      pr: 1,
      mb: 0.25,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      borderRadius: 'var(--radius-md)',
      position: 'relative',
      color: active ? 'var(--accent)' : 'var(--text-secondary)',
      bgcolor: active ? 'var(--accent-soft)' : 'transparent',
      transition: 'background 0.15s ease, color 0.15s ease',
      userSelect: 'none',
      fontFamily: 'Inter, sans-serif',
      '&:hover': {
        bgcolor: active ? 'var(--accent-soft)' : 'var(--surface-1)',
      },
      '&:focus-visible': {
        outline: '2px solid var(--accent)',
        outlineOffset: '-2px',
      },
      ...(active && {
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: '0 3px 3px 0',
          bgcolor: 'var(--accent)',
        },
      }),
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& svg': { fontSize: 16 } }}>
      {icon}
    </Box>
    <Typography
      sx={{
        flex: 1,
        fontWeight: active ? 600 : 500,
        fontSize: '13px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    {count !== undefined && count > 0 && (
      <Typography
        component="span"
        sx={{
          color: active ? 'var(--accent)' : 'var(--text-tertiary)',
          fontSize: '11px',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 500,
        }}
      >
        {count > 999 ? '999+' : count}
      </Typography>
    )}
  </Box>
);

const Sidebar: React.FC<SidebarProps> = ({ totalUnread, collapsed, onToggleCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [searchFocused, setSearchFocused] = useState(false);
  const [groups, setGroups] = useState<SubscriptionGroup[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCountsResponse | null>(null);

  const toggleCollapsed = onToggleCollapsed;

  const isArticlesRoute = location.pathname.startsWith('/articles');

  // Self-fetch groups + unread counts so the categories rail can drive URL state.
  useEffect(() => {
    if (!isArticlesRoute) return;
    let cancelled = false;
    (async () => {
      try {
        const [grpRes, unreadRes] = await Promise.all([
          groupApi.list(),
          articleApi.getUnreadCounts(30),
        ]);
        if (cancelled) return;
        const groupsList = (grpRes?.data?.data ?? []) as SubscriptionGroup[];
        setGroups(Array.isArray(groupsList) ? groupsList : []);
        setUnreadCounts(unreadRes?.data?.data ?? null);
      } catch {
        // silent — sidebar fetch is non-critical
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isArticlesRoute, location.search]);

  const selectedGroupId = searchParams.get('group');
  const favoriteFilter = searchParams.get('favorite') === 'true';
  const hideDuplicates = searchParams.get('hide_duplicates') === 'true';
  const allActive = isArticlesRoute && !selectedGroupId && !favoriteFilter;

  const setGroupParam = useCallback(
    (groupId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (groupId) next.set('group', groupId);
      else next.delete('group');
      next.delete('favorite');
      if (!isArticlesRoute) navigate(`/articles?${next.toString()}`);
      else setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, isArticlesRoute, navigate],
  );

  const toggleFavorite = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (favoriteFilter) next.delete('favorite');
    else {
      next.set('favorite', 'true');
      next.delete('group');
    }
    if (!isArticlesRoute) navigate(`/articles?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, favoriteFilter, isArticlesRoute, navigate]);

  const toggleHideDup = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (hideDuplicates) next.delete('hide_duplicates');
    else next.set('hide_duplicates', 'true');
    if (!isArticlesRoute) navigate(`/articles?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, hideDuplicates, isArticlesRoute, navigate]);

  const groupUnread = useCallback(
    (groupId: string) => {
      const entry = unreadCounts?.groups?.find((g) => g.group_id === groupId);
      return entry?.unread_count ?? 0;
    },
    [unreadCounts],
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const width = getSidebarWidth(collapsed);

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        background: 'var(--glass-bg)',
        borderRight: '1px solid var(--glass-border)',
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 1.5 : 2.5,
          py: 2,
          minHeight: 56,
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => navigate('/articles')}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
            P
          </Typography>
        </Box>
        {!collapsed && (
          <Typography
            sx={{
              ml: 1.5,
              fontWeight: 700,
              fontSize: 18,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            PlotLake
          </Typography>
        )}
      </Box>

      {/* Search bar */}
      {!collapsed && (
        <Box
          sx={{
            mx: 1.5,
            mb: 1,
            borderRadius: 'var(--radius-md)',
            background: 'var(--glass-bg-strong)',
            border: searchFocused ? '1px solid var(--accent)' : '1px solid var(--glass-border)',
            boxShadow: searchFocused ? '0 0 0 3px var(--accent-soft)' : 'none',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.5,
            transition: 'box-shadow 0.2s, border-color 0.2s',
          }}
        >
          <Search sx={{ fontSize: 16, color: 'var(--text-tertiary)', mr: 1, flexShrink: 0 }} />
          <InputBase
            placeholder="搜索…"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            sx={{
              flex: 1,
              fontSize: 13,
              color: 'var(--text-primary)',
              '& input::placeholder': { color: 'var(--text-tertiary)' },
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: 'var(--text-tertiary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              px: 0.5,
              lineHeight: 1.6,
              flexShrink: 0,
            }}
          >
            ⌘K
          </Typography>
        </Box>
      )}

      {collapsed && (
        <Tooltip title="搜索" placement="right">
          <Box
            sx={{
              mx: 'auto',
              mb: 1,
              width: 40,
              height: 40,
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { background: 'var(--surface-1)' },
            }}
          >
            <Search sx={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
          </Box>
        </Tooltip>
      )}

      {/* Folo-style horizontal icon-only main nav */}
      {!collapsed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            px: 1,
            py: 1,
            mb: 0.5,
            gap: 0.25,
          }}
        >
          <LayoutGroup id="sidebar-top-nav">
            {SIDEBAR_ROUTES.map((route) => {
              const active = isActive(route.path);
              const Icon = route.icon;
              const isArticles = route.path === '/articles';
              const count = isArticles ? totalUnread : 0;
              return (
                <Tooltip key={route.path} title={t(route.labelKey)} placement="bottom">
                  <Box
                    onClick={() => navigate(route.path)}
                    role="button"
                    aria-label={t(route.labelKey)}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      flex: 1,
                      height: 44,
                      minWidth: 32,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.25,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      color: active ? 'var(--accent)' : 'var(--text-secondary)',
                      transition: 'color 0.15s',
                      '&:hover .sidebar-hover-bg': {
                        background: active ? 'var(--accent-soft)' : 'var(--surface-1)',
                      },
                    }}
                  >
                    {active && (
                      <motion.div
                        layoutId="sidebar-active-bg"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 8,
                          background: 'var(--accent-soft)',
                          zIndex: 0,
                        }}
                        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                      />
                    )}
                    <Box
                      className="sidebar-hover-bg"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 'var(--radius-md)',
                        transition: 'background 0.15s',
                        zIndex: 0,
                      }}
                    />
                    <Icon sx={{ fontSize: 20, position: 'relative', zIndex: 1 }} />
                    {count > 0 && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          lineHeight: 1,
                          color: 'inherit',
                          fontVariantNumeric: 'tabular-nums',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {count > 99 ? '99+' : count}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </LayoutGroup>
        </Box>
      )}

      {/* Collapsed mode: vertical icon stack */}
      {collapsed && (
        <Box sx={{ px: 1, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {SIDEBAR_ROUTES.map((route) => {
            const active = isActive(route.path);
            const Icon = route.icon;
            const isArticles = route.path === '/articles';
            return (
              <Tooltip key={route.path} title={t(route.labelKey)} placement="right">
                <Box
                  onClick={() => navigate(route.path)}
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    background: active ? 'var(--accent-soft)' : 'transparent',
                    color: active ? 'var(--accent)' : 'var(--text-secondary)',
                    '&:hover': { background: active ? 'var(--accent-soft)' : 'var(--surface-1)' },
                  }}
                >
                  {isArticles && totalUnread > 0 ? (
                    <Badge
                      badgeContent={totalUnread}
                      color="error"
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 },
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Badge>
                  ) : (
                    <Icon sx={{ fontSize: 20 }} />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}

      <Box sx={{ flex: 1, px: collapsed ? 1 : 1.5, py: 0.5, overflowY: 'auto' }}>
        {/* Categories section — only on /articles, only when expanded */}
        {isArticlesRoute && !collapsed && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ height: '1px', bgcolor: 'var(--glass-border)', mx: 0.5, mb: 1.5 }} />
            <Typography
              sx={{
                px: 1.5,
                mb: 0.5,
                color: 'var(--text-tertiary)',
                fontSize: '10px',
                letterSpacing: '0.08em',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              分类
            </Typography>

            <CategoryItem
              active={allActive}
              icon={<Inbox />}
              label="全部"
              count={unreadCounts?.total_unread}
              onClick={() => setGroupParam(null)}
            />
            {groups.map((g) => (
              <CategoryItem
                key={g.id}
                active={selectedGroupId === g.id}
                icon={<Folder />}
                label={g.name}
                count={groupUnread(g.id)}
                onClick={() => setGroupParam(g.id)}
              />
            ))}

            <Box sx={{ height: '1px', bgcolor: 'var(--glass-border)', mx: 0.5, my: 1.5 }} />
            <Typography
              sx={{
                px: 1.5,
                mb: 0.5,
                color: 'var(--text-tertiary)',
                fontSize: '10px',
                letterSpacing: '0.08em',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              筛选
            </Typography>
            <CategoryItem
              active={favoriteFilter}
              icon={<Star />}
              label="收藏"
              count={unreadCounts?.total_favorites}
              onClick={toggleFavorite}
            />
            <CategoryItem
              active={hideDuplicates}
              icon={<ContentCopy />}
              label={hideDuplicates ? '显示重复' : '隐藏重复'}
              onClick={toggleHideDup}
            />
          </Box>
        )}
      </Box>

      {/* Bottom: user menu + theme + collapse toggle */}
      <Box
        sx={{
          px: collapsed ? 1 : 1.5,
          py: 1.5,
          borderTop: '1px solid var(--glass-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexDirection: collapsed ? 'column' : 'row',
        }}
      >
        <UserMenuInline collapsed={collapsed} />
        {!collapsed && <Box sx={{ flex: 1 }} />}
        <ThemeSwitcher />
        <Tooltip title={collapsed ? '展开侧边栏' : '折叠侧边栏'} placement="right">
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            sx={{
              color: 'var(--text-tertiary)',
              '&:hover': { color: 'var(--text-primary)', background: 'var(--surface-1)' },
            }}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Sidebar;
